import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, pivotData} from 'wc-lib';
import {format} from 'date-fns';

require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/table.svg');
require('wc-lib/icons/download.svg');
require('wc-lib/icons/graph.svg');
require('wc-lib/icons/infocircle.svg');

@Component({
  template: require('./severity-and-frequency-of-unserved-energy.html'),
  name: 'severity-and-frequency-of-unserved-energy',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class SeverityAndFrequencyOfUnservedEnergy extends Vue {
  data: Array<any> = [];
  filteredData: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  chartVisible = true;
  tableVisible = false;
  availableRegions: string[] = [];
  availablePeriods: Date[] = [];
  selectedRegion: string = 'NSW1';
  selectedPeriod: string = '';
  poe10Enabled: boolean = true;
  poe50Enabled: boolean = true;
  runDateTime: string = "";
  runNo: string = "";
  id: string;
  infoText: string = 'This figure shows all simulations where unserved energy occurred at some stage during a rolling one-year window. For each of these simulations, the value plotted represents the average number of half-hours where unserved energy occurred and the average size of unserved energy during these half-hour periods.';
  columns: Array<any> = [
    { title: 'Region', field: 'REGIONID' }, 
    { title: 'POE type', field: 'DEMAND_POE_TYPE' }, 
    { title: 'Average Amount of Unserved Energy', field: 'USE_ITERATION_EVENT_AVERAGE'}, 
    { title: 'Half hours', field: 'USE_ITERATION_EVENT_NUMBER' }
  ];

  get chartButtonType(): string {
    return this.chartVisible ? 'primary' : 'ghost';
  }

  get tableButtonType(): string {
    return this.tableVisible ? 'primary' : 'ghost';
  }

  chartConfig: any = {
    type: 'xy',
    theme: 'simpleblue',
    valueAxes: [ {
      position: 'bottom',
      title: 'Average Amount of Unserved Energy (MW)',
      axisAlpha: 0,
      minimum: 0,
      integersOnly: true
    }, {
      position: 'left',
      title: 'Number of half hours with Unserved Energy',
      axisAlpha: 0,
      integersOnly: true
    } ],
    startDuration: 0,
    graphs: [ {
      bullet: 'round',
      balloonText: 'Category: [[Category]]\r\nAverage Amount of Unserved Energy: [[USE_ITERATION_EVENT_AVERAGE]] MW\r\nHalf hours: [[USE_ITERATION_EVENT_NUMBER]]',
      bulletBorderAlpha: 1,
      bulletBorderThickness: 2,
      bulletAlpha: 1,
      bulletSize: 10,
      lineAlpha: 0,
      xField: 'USE_ITERATION_EVENT_AVERAGE',
      yField: 'USE_ITERATION_EVENT_NUMBER',
      colorField: 'Color'
    } ],
    legend: {
      equalWidths: true,
      position: 'top',
      data: [ { title: 'POE 10', color: '#00cc00' }, { title: 'POE 50', color: '#cc0000' }],
      clickMarker: this.clickMarker,
      clickLabel: this.clickMarker
    },
    chartCursor: {}
  };

  clickMarker(event: any) {
    if (event.legendTextReal === 'POE 10') {
      this.poe10Enabled = !this.poe10Enabled;
      event.hidden = !event.hidden;
    } else {
      this.poe50Enabled = !this.poe50Enabled;
      event.hidden = !event.hidden;
    }
  }

  async created() {
    this.id = await DataManager.addEndPoint('../../api/report/MTPASA_CHART_3', undefined, undefined, 'MTPASA_CHART_3', undefined, 'yyyy-MM-ddTHH:mm:ss', ['RUN_DATETIME', 'PERIOD_ENDING']);
    this.dynamicView = DataManager.getDynamicView(this.id, 'events');
    this.dynamicView.removeFilters();
    let allData = this.dynamicView.data();
    this.runDateTime = format(allData[0].RUN_DATETIME, 'yyyy-MM-dd HH:mm:ss');
    this.runNo = allData[0].RUN_NO;
    allData.forEach( x => {
      x.YEAR = format(x.PERIOD_ENDING, 'yyyy');
      /*if(this.availableRegions.indexOf(x.REGIONID) === -1) {
        this.availableRegions.push(x.REGIONID);
      }*/
      //if(!!!this.availablePeriods.find(item => item.getTime() == x.PERIOD_ENDING.getTime())) {
      if(this.availablePeriods.indexOf(x.YEAR) === -1) {
        this.availablePeriods.push(x.YEAR);
      }
      this.selectedPeriod = x.YEAR;
      this.selectedRegion = x.REGIONID;
    });
    this.applyFilter();
    this.availableRegions.push('NSW1');
    this.availableRegions.push('QLD1');
    this.availableRegions.push('SA1');
    this.availableRegions.push('TAS1');
    this.availableRegions.push('VIC1');
  }

  applyFilter() {
    this.dynamicView.removeFilters();
    this.dynamicView.applyFind({ 'REGIONID': this.selectedRegion, 'YEAR': this.selectedPeriod, 'DEMAND_POE_TYPE': { '$in' : this.poeFilter }});
    this.data = this.dynamicView.data();
    this.filteredData = this.data;
    this.filteredData.forEach(x => {
      x.Color = x.DEMAND_POE_TYPE === 'POE10' ? '#00cc00' : '#cc0000';
    });
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  @Watch('selectedPeriod')
  onPeriodChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  @Watch('poe10Enabled')
  @Watch('poe50Enabled')
  onPoeEnabled(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  get poeFilter() {
    let filter = [];
    if (this.poe10Enabled) {
      filter.push('POE10');
    }
    if (this.poe50Enabled) {
      filter.push('POE50');
    }
    return filter;
  }

  chartClick() {
    this.chartVisible = true;
    this.tableVisible = false;
  }

  tableClick() {
    this.tableVisible = true;
    this.chartVisible = false;
  }

  downloadClick() {
    DownloadHelper.download('MTPASA_CHART_3', this.filteredData, this.columns);
  }
}



// WEBPACK FOOTER //
// src/views/electricity/nem/mtpasa/severity-and-frequency-of-unserved-energy.ts